import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Devotionals() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentDevotional, setCurrentDevotional] = useState(1);
  const navigate = useNavigate();

  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  // Função para gerar devocionais automaticamente baseados na data
  const generateDevotional = (dayOfYear: number) => {
    const devotionalTemplates = [
      {
        themes: ['Novo Começo', 'Esperança', 'Fé', 'Amor de Deus', 'Perdão', 'Gratidão', 'Paz', 'Sabedoria', 'Força', 'Confiança'],
        verses: [
          'Portanto, se alguém está em Cristo, é nova criatura. As coisas antigas já passaram; eis que se fizeram novas! - 2 Coríntios 5:17',
          'E sabemos que todas as coisas contribuem juntamente para o bem daqueles que amam a Deus, daqueles que são chamados por seu decreto. - Romanos 8:28',
          'Confia no Senhor de todo o teu coração e não te estribes no teu próprio entendimento. - Provérbios 3:5',
          'Porque Deus amou o mundo de tal maneira que deu o seu Filho unigênito, para que todo aquele que nele crê não pereça, mas tenha a vida eterna. - João 3:16',
          'Se confessarmos os nossos pecados, ele é fiel e justo para nos perdoar os pecados e nos purificar de toda injustiça. - 1 João 1:9',
          'Em tudo dai graças, porque esta é a vontade de Deus em Cristo Jesus para convosco. - 1 Tessalonicenses 5:18',
          'E a paz de Deus, que excede todo o entendimento, guardará os vossos corações e os vossos sentimentos em Cristo Jesus. - Filipenses 4:7',
          'Se algum de vós tem falta de sabedoria, peça-a a Deus, que a todos dá liberalmente e sem repreensão; e ser-lhe-á dada. - Tiago 1:5',
          'Posso todas as coisas em Cristo que me fortalece. - Filipenses 4:13',
          'Lança o teu cuidado sobre o Senhor, e ele te susterá; nunca permitirá que o justo seja abalado. - Salmos 55:22'
        ]
      }
    ];

    const template = devotionalTemplates[0];
    const themeIndex = (dayOfYear - 1) % template.themes.length;
    const verseIndex = (dayOfYear - 1) % template.verses.length;
    
    const date = new Date(2024, 0, dayOfYear);
    const day = date.getDate();
    const month = months[date.getMonth()];

    return {
      id: dayOfYear,
      date: `${day} de ${month}`,
      title: template.themes[themeIndex],
      verse: template.verses[verseIndex],
      content: `Hoje refletimos sobre ${template.themes[themeIndex].toLowerCase()}. A Palavra de Deus nos ensina que cada dia é uma nova oportunidade de crescer em nossa fé e experimentar o amor incondicional do Senhor. Quando enfrentamos desafios, podemos ter a certeza de que Deus está conosco, guiando nossos passos e fortalecendo nosso coração. Este versículo nos lembra da fidelidade de Deus e de como Ele trabalha em nossas vidas para o nosso bem e para a Sua glória. Que possamos hoje viver essa verdade de forma prática, permitindo que o Espírito Santo nos transforme e nos use como instrumentos de bênção na vida de outras pessoas.`,
      prayer: `Senhor, obrigado por este novo dia e pela oportunidade de crescer em Ti. Ajuda-me a viver os princípios da Tua Palavra e a ser uma bênção para todos ao meu redor. Que eu possa experimentar mais do Teu amor e da Tua paz hoje. Guia meus passos e usa-me para Tua glória. Em nome de Jesus, amém.`,
      application: `Hoje, dedique alguns minutos para meditar neste versículo e pensar em como você pode aplicar essa verdade em sua vida prática. Ore por uma situação específica que você está enfrentando e confie que Deus tem o controle de todas as coisas.`
    };
  };

  // Atualizar devocional baseado na data atual
  useEffect(() => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    setCurrentDevotional(dayOfYear);
    setSelectedDate(today);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const getDayOfYear = (date: Date) => {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date.getTime() - start.getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const currentDayOfYear = getDayOfYear(selectedDate);
  const devotional = generateDevotional(currentDevotional);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-lg border-b-4 border-gradient-to-r from-blue-600 to-purple-600">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://static.readdy.ai/image/8fbfc975813ef59021c9f16b313e86f9/f5810bc36c96b4c4a03e9a9aa341e507.png"
                alt="Assembleia de Deus - Missão Europa"
                className="w-20 h-20 sm:w-24 sm:h-24 lg:w-32 lg:h-32 object-contain shadow-2xl rounded-2xl border-4 border-indigo-200"
              />
              <div className="text-center flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
                  <span style={{ fontFamily: '"Agnes", serif' }}>IGREJA AD MISSÃO EUROPA CHURCH</span>
                </h1>
                <p className="text-sm text-gray-600">Church Portugal</p>
              </div>
            </div>
            
            <nav className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/bible')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-book-line"></i>
                <span>Bíblia</span>
              </button>
              <button 
                onClick={() => navigate('/devotionals')}
                className="flex items-center space-x-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                <span>Devocionais</span>
              </button>
              <button 
                onClick={() => navigate('/studies')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-graduation-cap-line"></i>
                <span>Estudos</span>
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line"></i>
                <span>Sair</span>
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <img 
          src="https://public.readdy.ai/ai/img_res/ba80cd68f3ab4b6297f63d65618e1567.jpg"
          alt="Devocional"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-2">Devocionais Diários</h2>
            <p className="text-xl opacity-90">365 dias de comunhão com Deus</p>
            <p className="text-lg opacity-75 mt-2">Devocional automático baseado na data de hoje</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Calendário e Navegação */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-800 mb-4">Selecionar Data</h3>
              
              <div className="mb-4">
                <input
                  type="date"
                  value={selectedDate.toISOString().split('T')[0]}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    setSelectedDate(newDate);
                    const dayOfYear = getDayOfYear(newDate);
                    setCurrentDevotional(dayOfYear);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="text-center mb-4">
                <p className="text-sm text-gray-600">Dia do ano</p>
                <p className="text-2xl font-bold text-blue-600">{currentDevotional}</p>
              </div>

              <div className="space-y-2">
                <button
                  onClick={() => {
                    const newDay = Math.max(1, currentDevotional - 1);
                    setCurrentDevotional(newDay);
                    const newDate = new Date(2024, 0, newDay);
                    setSelectedDate(newDate);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Anterior</span>
                </button>
                <button
                  onClick={() => {
                    const newDay = Math.min(365, currentDevotional + 1);
                    setCurrentDevotional(newDay);
                    const newDate = new Date(2024, 0, newDay);
                    setSelectedDate(newDate);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <span>Próximo</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
                <button
                  onClick={() => {
                    const today = new Date();
                    const dayOfYear = getDayOfYear(today);
                    setCurrentDevotional(dayOfYear);
                    setSelectedDate(today);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                >
                  <i className="ri-calendar-line"></i>
                  <span>Hoje</span>
                </button>
              </div>
            </div>

            {/* Progresso Anual */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Progresso Anual</h3>
              <div className="mb-2">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span>Devocionais lidos</span>
                  <span>{currentDevotional}/365</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentDevotional / 365) * 100}%` }}
                  ></div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {Math.round((currentDevotional / 365) * 100)}% concluído
              </p>
            </div>

            {/* Status Automático */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl shadow-lg p-6 mt-6 border-l-4 border-green-500">
              <h3 className="text-lg font-semibold text-green-800 mb-2 flex items-center">
                <i className="ri-refresh-line mr-2"></i>
                Sistema Automático
              </h3>
              <p className="text-sm text-green-700">
                ✅ Devocionais gerados automaticamente<br/>
                ✅ Baseado na data atual<br/>
                ✅ 365 dias de conteúdo<br/>
                ✅ Atualização diária
              </p>
            </div>
          </div>

          {/* Conteúdo do Devocional */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-blue-600 font-medium">{devotional.date}</span>
                  <div className="flex items-center space-x-2">
                    <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <i className="ri-bookmark-line"></i>
                    </button>
                    <button className="p-2 text-gray-400 hover:text-blue-600 transition-colors">
                      <i className="ri-share-line"></i>
                    </button>
                  </div>
                </div>
                <h1 className="text-3xl font-bold text-gray-800 mb-4">{devotional.title}</h1>
              </div>

              {/* Versículo do Dia */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-600 p-6 mb-8 rounded-r-lg">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="ri-double-quotes-l text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="text-lg text-gray-800 font-medium leading-relaxed mb-2">
                      {devotional.verse.split(' - ')[0]}
                    </p>
                    <p className="text-blue-600 font-semibold">
                      {devotional.verse.split(' - ')[1]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reflexão */}
              <div className="mb-8">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-lightbulb-line text-yellow-500 mr-2"></i>
                  Reflexão
                </h3>
                <p className="text-gray-700 leading-relaxed text-lg">
                  {devotional.content}
                </p>
              </div>

              {/* Oração */}
              <div className="mb-8 bg-purple-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-hands-line text-purple-600 mr-2"></i>
                  Oração do Dia
                </h3>
                <p className="text-gray-700 leading-relaxed italic">
                  {devotional.prayer}
                </p>
              </div>

              {/* Aplicação Prática */}
              <div className="mb-8 bg-green-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <i className="ri-checkbox-circle-line text-green-600 mr-2"></i>
                  Aplicação Prática
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {devotional.application}
                </p>
              </div>

              {/* Navegação */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setCurrentDevotional(Math.max(1, currentDevotional - 1))}
                  className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  <span>Devocional Anterior</span>
                </button>
                
                <span className="text-sm text-gray-500">
                  Devocional {currentDevotional} de 365
                </span>
                
                <button
                  onClick={() => setCurrentDevotional(Math.min(365, currentDevotional + 1))}
                  className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  <span>Próximo Devocional</span>
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
